"use client";

import { flexRender } from "@tanstack/react-table";
import { Filter, GripVertical, Pencil } from "lucide-react";
import { PROCESSING_MODE_OBJ, SORT_DIRECTION_OBJ } from "../domain";



import { useSmartTableModelContext } from "./smart-table-model-context";

export function SmartTableHead<TData>() {
  const m = useSmartTableModelContext<TData>();
  const { filters, sort, columnMeta, enableColumnResizing, enableColumnReorder, setShowFilters, setFilterPopoverColumn, t, headerMouse, actionColumnIds, displayData, table, toggleSort, bulkEditMode, selectedRowIds, isBulkSaving, getRowId, toggleAllVisible, setDraggedColumn, stickyIds, handleColumnDrop } = m;
  return (
            <thead className="[&_tr]:border-b">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b transition-colors">
                  {bulkEditMode && (
                    <th
                      className="h-12 w-10 px-2 text-center align-middle font-medium text-text-secondary"
                      aria-label={t("table.bulkEdit.aria.rowSelect")}
                    >
                      <input
                        type="checkbox"
                        checked={
                          displayData.length > 0 &&
                          displayData.every((r) =>
                            selectedRowIds.has(getRowId(r)),
                          )
                        }
                        onChange={() => toggleAllVisible(displayData)}
                        aria-label={t("table.bulkEdit.aria.rowSelect")}
                        disabled={isBulkSaving}
                      />
                    </th>
                  )}
                  {headerGroup.headers.map((header) => {
                    const columnId = header.column.id;
                    const sortClause = sort.find((s) => s.column === columnId);
                    const sortIdx = sort.findIndex(
                      (s) => s.column === columnId,
                    );
                    const meta = header.column.columnDef.meta;
                    const colMeta = columnMeta[columnId];
                    const isActionCol = actionColumnIds.includes(columnId);
                    const sortable =
                      !isActionCol &&
                      meta?.sortable !== PROCESSING_MODE_OBJ.NONE;
                    const stickyIdx = stickyIds.indexOf(columnId);
                    const isSticky = stickyIdx !== -1;
                    const headerBg =
                      colMeta?.headerColor ?? "var(--background, #fff)";
                    let stickyLeft = 0;
                    if (isSticky) {
                      for (const h of headerGroup.headers) {
                        if (h === header) break;
                        if (stickyIds.includes(h.column.id)) {
                          stickyLeft += h.getSize();
                        }
                      }
                    }
                    return (
                      <th
                        key={header.id}
                        className={`relative h-12 px-4 text-left align-middle font-medium text-text-secondary ${sortable ? "cursor-pointer" : "cursor-text"}`}
                        data-sticky-col={isSticky ? "" : undefined}
                        data-sticky-left={isSticky ? stickyLeft : undefined}
                        style={{
                          width: header.getSize(),
                          ...(headerBg ? { backgroundColor: headerBg } : {}),
                          ...(colMeta?.headerTextColor
                            ? { color: colMeta.headerTextColor }
                            : {}),
                        }}
                        onMouseDown={(e) => {
                          headerMouse.current = {
                            x: e.clientX,
                            y: e.clientY,
                            down: true,
                          };
                        }}
                        onMouseUp={(e) => {
                          headerMouse.current = null;
                          if (e.button !== 0) return;
                          // Кнопки внутри заголовка (воронка фильтра) не должны менять сортировку.
                          if (e.target instanceof Element && e.target.closest("button") !== null) return;
                          const sel = window.getSelection();
                          if (sel && sel.toString().length > 0) {
                            e.preventDefault();
                            return;
                          }
                          sortable && toggleSort(columnId, e.shiftKey);
                        }}
                        onDoubleClick={(e) => {
                          e.preventDefault();
                          const range = document.createRange();
                          range.selectNodeContents(e.currentTarget);
                          const sel = window.getSelection();
                          if (sel) {
                            sel.removeAllRanges();
                            sel.addRange(range);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (
                            sortable &&
                            (e.key === "Enter" || e.key === " ")
                          ) {
                            e.preventDefault();
                            toggleSort(columnId, e.shiftKey);
                          }
                        }}
                        tabIndex={sortable ? 0 : -1}
                        role={sortable ? "button" : undefined}
                        aria-label={t("table.sort.by")}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => handleColumnDrop(columnId)}
                      >
                        {enableColumnReorder && !isSticky && !isActionCol && (
                          <span
                            draggable
                            onDragStart={() => setDraggedColumn(columnId)}
                            onClick={(e) => e.stopPropagation()}
                            className="absolute left-0.5 top-1/2 -translate-y-1/2 cursor-grab text-text-secondary opacity-0 hover:opacity-100"
                            aria-label={t("reorderColumn")}
                          >
                            <GripVertical className="h-4 w-4" />
                          </span>
                        )}
                        <span
                          className="header-text"
                          onMouseUp={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            const range = document.createRange();
                            range.selectNodeContents(e.currentTarget);
                            const sel = window.getSelection();
                            if (sel) {
                              sel.removeAllRanges();
                              sel.addRange(range);
                            }
                          }}
                          onDoubleClick={(e) => e.stopPropagation()}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                          {header.column.columnDef.meta?.editable && (
                            <Pencil
                              className="ml-1 inline h-3 w-3 text-[color:var(--table-accent,var(--ui-color-primary))]"
                              aria-label={t("table.editable")}
                            />
                          )}
                        </span>
                        {sortClause && (
                          <span className="ml-1">
                            {sortClause.direction === SORT_DIRECTION_OBJ.ASC
                              ? " \u25B2"
                              : " \u25BC"}
                            {sort.length > 1 && (
                              <sup className="text-xs">{sortIdx + 1}</sup>
                            )}
                          </span>
                        )}
                        {!isActionCol &&
                          meta?.filterable !== PROCESSING_MODE_OBJ.NONE && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowFilters(true);
                                setFilterPopoverColumn(columnId);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "f") {
                                  e.preventDefault();
                                  setShowFilters(true);
                                  setFilterPopoverColumn(columnId);
                                }
                              }}
                              className="ml-1 inline-flex items-center"
                              aria-label={t("table.filters.aria.toggle")}
                              tabIndex={0}
                            >
                              <Filter
                                className={`h-3 w-3 ${
                                  filters.some((f) => f.column === columnId)
                                    ? "text-primary"
                                    : "text-text-secondary"
                                }`}
                              />
                            </button>
                          )}
                        {enableColumnResizing && (
                          <span
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            onClick={(e) => e.stopPropagation()}
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-border opacity-0 hover:opacity-100"
                            role="separator"
                            aria-orientation="vertical"
                            aria-label={t("table.resize.column")}
                            tabIndex={0}
                          />
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
  );
}
